import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import { useAdditionalPropertyValues } from '../hooks/useAdditionalPropertyValues';
import { usePropertyDefinition } from '../hooks/usePropertyDefinition';
import { usePropertyValue } from '../hooks/usePropertyValue';
import { useObjectTypeId } from '../hooks/useObjectTypeId';
import { useHomeCurrency } from '../hooks/useHomeCurrency';
import { EMPTY_PROPERTY_VALUE } from '../../constants/EmptyPropertyValue';
import { parseNumericValue } from '../../utils/parseNumericValue'; // @ts-expect-error dep untyped

import { FormattedCurrency as FormattedFractionalCurrency } from 'I18n/utils/internal/light/numberFormatters';
import isFractionalCurrencySupported from '../utils/isFractionalCurrencySupported';
import isFullPrecisionCurrencyRequired from '../utils/isFullPrecisionCurrencyRequired';

function getShouldSimplifyCurrency(objectTypeId, currencyProps, legacySimplifyCurrency) {
  if (currencyProps.simplifyCurrency !== undefined || legacySimplifyCurrency !== undefined) {
    return currencyProps.simplifyCurrency || legacySimplifyCurrency;
  }

  return !isFullPrecisionCurrencyRequired(objectTypeId);
}

const defaultFractionalCurrencyProps = {
  precision: 6,
  stripInsignificantZeros: true
};

const FormattedPropertyCurrencyV2 = props => {
  const {
    className,
    placeholder = EMPTY_PROPERTY_VALUE,
    legacySimplifyCurrency,
    currencyProps = {}
  } = props;
  const objectTypeId = useObjectTypeId();
  const propertyDefinition = usePropertyDefinition();
  const value = usePropertyValue();
  const propertiesToFetch = propertyDefinition.currencyPropertyName ? [propertyDefinition.currencyPropertyName] : [];
  const [subjectCurrencyCode] = useAdditionalPropertyValues(propertiesToFetch);
  const {
    data: homeCurrencyCode
  } = useHomeCurrency();
  const currencyCode = currencyProps.currencyCode || subjectCurrencyCode || homeCurrencyCode;
  const currencyValue = parseNumericValue(value);
  const shouldSimplifyCurrency = getShouldSimplifyCurrency(objectTypeId, currencyProps, legacySimplifyCurrency);

  if (currencyValue === null) {
    return /*#__PURE__*/_jsx("span", {
      className: className,
      children: placeholder
    });
  }

  let CurrencyComponent = FormattedCurrency;
  let fractionalCurrencyProps;

  if (isFractionalCurrencySupported(objectTypeId)) {
    CurrencyComponent = FormattedFractionalCurrency;
    fractionalCurrencyProps = defaultFractionalCurrencyProps;
  }

  if (!currencyCode) {
    return /*#__PURE__*/_jsx("span", {
      className: className,
      children: /*#__PURE__*/_jsx(CurrencyComponent, Object.assign({}, currencyProps, {}, fractionalCurrencyProps, {
        simplifyCurrency: shouldSimplifyCurrency,
        value: currencyValue // Intentionally hide the currency symbol while loading,
        // or if we have an error loading the home currency
        // NOTE: use a blank space instead of an empty string to silence the
        // warning: 'I18n: Missing currencyCode in call to formatCurrency().'
        // It looks identical (browser collapses leading whitespace)
        ,
        unit: " "
      }))
    });
  }

  return /*#__PURE__*/_jsx("span", {
    className: className,
    "data-selenium-test": "currency-component-loaded",
    children: /*#__PURE__*/_jsx(CurrencyComponent, Object.assign({}, currencyProps, {}, fractionalCurrencyProps, {
      currencyCode: currencyCode,
      simplifyCurrency: shouldSimplifyCurrency,
      value: currencyValue
    }))
  });
};

FormattedPropertyCurrencyV2.displayName = 'FormattedPropertyCurrencyV2';
FormattedPropertyCurrencyV2.propTypes = {
  className: PropTypes.string,
  currencyProps: PropTypes.object,
  placeholder: PropTypes.node,
  legacySimplifyCurrency: PropTypes.bool
};
export default FormattedPropertyCurrencyV2;